import { Patient } from 'api/account/patients.type';
import {
  MedicalHistorySurveySlug,
  SymptomAssessmentSurveySlug,
} from 'helpers/survey-constants/SurveyScoreRanges';

import { PatientVital } from './patientVital.type';
import {
  ISurveyQuestionItem,
  ISurveyResponse,
  ISurveyResponseAnswer,
} from './types';

export interface ConsultVideo {
  id: string;
  patientConsultId: string;
  ziggeoToken: string | undefined;
  videoSubject: string | string[] | undefined;
  creatorType: 'Patient' | 'Account';
  creatorId: string;
  status: 'created' | 'skipped';
  createdAt: string;
  updatedAt: string;
}

export interface MinimalSurvey<
  Slugs = SymptomAssessmentSurveySlug | MedicalHistorySurveySlug,
> {
  id: string;
  slug: Slugs;
  name: string;
  description: string;
}

export interface Provider {
  firstName: string;
  lastName: string;
  avatarUrlDefault: string | undefined;
  avatarUrlSmall: string | undefined;
  credential: string | null;
}

export interface RxOrder {
  id: string;
  patientConsultId: string;
  signature: string; // i.e. "1 Tablet, Twice daily for 15 days"
  drugName: string; // i.e. "Adderal 5MG PO Tablet"
  label: string; // i.e. ""
  notes: string; // Notes from the pharmacy
  dispenseAmount: string; // i.e. "30 Tablets"
  dispenseUnits: string; // i.e. "Tablets"
  drugCode: string; // i.e "051898"
  drugCodeSystem: string; // i.e. "Medispan"
  duration: number;
  earliestFillDate: string; // new Date-able
  frequency: string; // "Q4H", "BID", etc frequency lingo
  quantity: number;
  refills: number;
  status: string; // is really an enum
  substitutionAllowed: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TherapyRecommendation {
  id: string;
  notes: string;
  name: string;
  patientConsultId: string;
  therapyRecommendationOptionId: string;
}

export type PatientState =
  | 'patient_initialized'
  | 'in_progress'
  | 'video_record_needed'
  | 'patient_completed'
  | 'cannot_treat'
  | 'video_record_needed';

export type PaymentState =
  | 'unpaid'
  | 'patient_declined'
  | 'payment_failed'
  | 'paid'
  | 'payment_method_saved'
  | 'free';

export type ProviderState =
  | 'initialized'
  | 'ready_for_review'
  | 'reviewed'
  | 'diagnosed'
  | 'therapized'
  | 'medicated'
  | 'provider_completed';

export type PlanTitle =
  | 'COACHING-PLAN'
  | 'COACHING-AND-THERAPY-PLAN'
  | 'PSYCH-LED-PLAN';

export enum PlanTypeDTO {
  C0 = 'C0',
  C1 = 'C1',
  C2 = 'C2',
  C3 = 'C3',
  C3HS = 'C3-HS',
  C1M = 'C1M',
}

export type PlanType = `${PlanTypeDTO}`;

export type VideoPlanType = PlanTypeDTO.C3 | PlanTypeDTO.C3HS;

const VideoPlanLookup = {
  [PlanTypeDTO.C3]: PlanTypeDTO.C3,
  [PlanTypeDTO.C3HS]: PlanTypeDTO.C3HS,
  [PlanTypeDTO.C1M]: PlanTypeDTO.C1M,
} as const;

export function isVideoPlan(plan: PlanType): plan is VideoPlanType {
  return !!(plan in VideoPlanLookup);
}

export interface PatientConsult {
  id: string;
  patientId: string;
  providerCompletedAt: string | null;
  providerNotes: string;
  sentToPcP: boolean;
  skipCreditCard: boolean;
  skipConsultVideo: boolean;
  isBaseline: boolean;
  patientState: PatientState;
  paymentState: PaymentState;
  providerState: ProviderState;
  patientCompletedAt: string | null;
  pdfUrl: string | null;
  hasNpClinicalIntakeScheduled: boolean;

  /**
   * Order of this PatientConsult in a baseline -> followup series
   * An ordinal of 0 is the baseline/intake/initial consult
   * except in the case of a reactivation, in which case the consult.isBaseline
   * will be true, but the ordinal will likely be some higher number
   */
  ordinal: number;
  /**
   * If this consult is a followup, it will have a previousConsultId
   */
  previousConsultId: string | null;

  symptomAssessmentSurveyResponses: (ISurveyResponse & {
    survey: MinimalSurvey<SymptomAssessmentSurveySlug>;
    surveyResponseAnswers: ISurveyResponseAnswer & {
      surveyQuestion: ISurveyQuestionItem;
    };
  })[];
  symptomAssessmentSurveyResponseIds: {
    caregiver?: string[];
    child?: string[];
  };
  medicalHistorySurveyResponse: ISurveyResponse & {
    survey: MinimalSurvey<MedicalHistorySurveySlug>;
    surveyResponseAnswers: (ISurveyResponseAnswer & {
      id: string;
      surveyQuestion: ISurveyQuestionItem;
    })[];
  };
  patient: Patient;
  patientVitals: PatientVital;
  provider: Provider;
  // The plan (Coaching vs Full) that was chosen by the patient
  chosenPlan: PlanTitle;
  // The plan that we recommend that they choose
  recommendedPlan: PlanTitle;
  subscribedPlan: PlanType;
  cannotTreat: boolean | null;

  videoClientToken: string;
  videos: ConsultVideo[];

  // TODO: Figure out the type here
  rxOrders: RxOrder[];
  medicationNotesForPatient: string | null;

  therapyRecommendations: TherapyRecommendation[];

  createdAt: string;
  updatedAt: string;
  benefits: [string];
  currentState: string;
}

export enum ScoreType {
  Cutpoints = 'cutpoints',
  Category = 'category',
}

interface BaseScoreEvaluation {
  name: string;
  description: string;
  scoreName: string;
  type: ScoreType;
  label: string;
  subName?: string;
}

export interface CutpointScoreEvaluation extends BaseScoreEvaluation {
  type: ScoreType.Cutpoints;
  cutpoints: { min: number; max: number; ordinal: number }[];
  score: number;
}

export interface CategoryScoreEvaluation extends BaseScoreEvaluation {
  type: ScoreType.Category;
}

export type ScoreEvaluation = CutpointScoreEvaluation | CategoryScoreEvaluation;
